import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, TrendingUp, Package, Sparkles, ArrowRight, ArrowUpRight,
  Clock, DollarSign, Phone, Menu, X, Zap, Target,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import PlugitAIChat from "@/pages/PlugitAIChat";
import MobilePocketPopup from "@/pages/MobilePocketPopup";

// ─── Palette — pure black canvas, blood-red accents ─────────────────────────
const INK      = "#050505";        // pure near-black canvas
const PANEL    = "#0B0B0F";        // panel background
const BLOOD    = "#DC2626";        // blood red (Tailwind red-600)
const BLOOD_HI = "#EF4444";        // brighter red for hover / highlights
const RED_LOW  = "#450A0A";        // deep red for gradients
const RED_GLOW = "rgba(220,38,38,0.35)";
const OFFWHITE = "#F5F5F5";
const DIM      = "rgba(245,245,245,0.7)";
const FADE     = "rgba(245,245,245,0.5)";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Kit" },
  { to: "/case-studies/miss-rodriguez", label: "Case study" },
  { to: "/blog", label: "Blog" },
  { to: "#free-bot", label: "Free Bot", primary: true },
];

const BOTS = [
  {
    key: "chat",
    icon: MessageSquare,
    title: "Chat Bot",
    line1: "Answer instantly.",
    line2: "Capture every lead.",
    body: "Never miss a visitor at 2 AM. Every chat becomes a qualified lead in your CRM the second they hit enter.",
  },
  {
    key: "sales",
    icon: TrendingUp,
    title: "Sales Bot",
    line1: "Follow up. Close more.",
    line2: "Increase your revenue.",
    body: "Drips, quotes, and closes while you sleep. Consultative — not spammy. Handles objections like a 20-year sales floor.",
  },
  {
    key: "inventory",
    icon: Package,
    title: "Inventory Bot",
    line1: "Track. Manage. Optimize.",
    line2: "Never run out. Never overstock.",
    body: "Watches your stock 24/7, reorders on threshold, and flags the SKUs bleeding money before your accountant does.",
  },
];

const PROOF = [
  { icon: Clock,     label: "Save Time" },
  { icon: DollarSign,label: "Lower Costs" },
  { icon: TrendingUp,label: "Scale Faster" },
];

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}

export default function BotsPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const origin = process.env.REACT_APP_SITE_URL || window.location.origin;
    document.title =
      "Bots Are Changing Lives — Get One Free | PlugitAI";
    upsertMeta(
      "description",
      "Chat bot, sales bot, inventory bot — free consult and demo build. Whatever process is slowing you down, an AI twin from PlugitAI is the answer. Save time. Lower costs. Scale faster."
    );
    upsertMeta("robots", "index,follow,max-image-preview:large");
    upsertMeta("og:title", "Bots Are Changing Lives — Get One Free | PlugitAI", true);
    upsertMeta("og:description", "It'll change yours if you let it. Chat bot · Sales bot · Inventory bot — pick your process, get a free demo build.", true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", `${origin}/bots`, true);
    upsertCanonical(`${origin}/bots`);
  }, []);

  return (
    <div
      className="min-h-screen w-full pb-24 md:pb-0 overflow-x-hidden"
      style={{
        background: INK,
        color: OFFWHITE,
        fontFamily: "'Inter', 'Cabinet Grotesk', system-ui, sans-serif",
      }}
      data-testid="bots-page"
    >
      {/* Ambient scan-line + red glow bg */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(1200px 700px at 85% 10%, ${RED_GLOW}, transparent 60%),
                       radial-gradient(900px 500px at 15% 90%, rgba(220,38,38,0.14), transparent 65%),
                       radial-gradient(700px 400px at 50% 50%, rgba(220,38,38,0.05), transparent 70%)`,
        }}
      />
      {/* Subtle scan-line texture */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.06]"
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0px, rgba(255,255,255,0.5) 1px, transparent 1px, transparent 3px)",
        }}
      />

      <TopNav menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      {/* HERO — matches the black-and-red reference */}
      <section className="relative z-10 pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1.15fr,0.85fr] items-center gap-10 md:gap-14">
          {/* LEFT — headline */}
          <div className="relative">
            {/* Red kicker */}
            <div className="mb-5 md:mb-6 flex items-center gap-3">
              <span
                className="inline-block h-[2px] w-10"
                style={{ background: BLOOD }}
              />
              <span
                className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.28em]"
                style={{ color: BLOOD_HI }}
              >
                Tools that work · AI that works for you
              </span>
            </div>

            {/* MAIN HEADLINE — user-mandated title */}
            <h1
              className="font-black tracking-[-0.015em] leading-[0.94] uppercase"
              style={{ fontSize: "clamp(2.4rem, 6.2vw, 5.6rem)", color: OFFWHITE }}
              data-testid="bots-h1"
            >
              Bots are{" "}
              <span style={{
                color: BLOOD,
                textShadow: `0 0 40px ${RED_GLOW}, 0 0 2px rgba(220,38,38,0.8)`,
              }}>
                changing lives.
              </span>{" "}
              <br className="hidden md:block" />
              It&apos;ll change{" "}
              <span style={{
                background: `linear-gradient(90deg, ${BLOOD_HI} 0%, ${BLOOD} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: `drop-shadow(0 0 24px ${RED_GLOW})`,
              }}>
                yours
              </span>{" "}
              if you let it.
            </h1>

            <p
              className="mt-6 md:mt-7 text-base md:text-lg leading-relaxed max-w-[560px]"
              style={{ color: DIM }}
            >
              <strong className="text-white">Chat Bot. Sales Bot. Inventory Bot.</strong>{" "}
              Think of the process that gives you the{" "}
              <span style={{ color: BLOOD_HI, fontWeight: 700 }}>most problems</span>?{" "}
              That&apos;s the one we hand to your AI twin. And your first demo build is{" "}
              <span
                className="px-1.5"
                style={{
                  background: BLOOD,
                  color: "white",
                  borderRadius: 4,
                  fontWeight: 900,
                  letterSpacing: "0.04em",
                }}
              >
                FREE
              </span>
              .
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3" id="free-bot">
              <a
                href="tel:+12148155966"
                className="group relative inline-flex items-center gap-3 pl-4 pr-5 h-14 rounded-xl overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${BLOOD} 0%, ${RED_LOW} 100%)`,
                  boxShadow: `0 24px 60px -20px rgba(220,38,38,0.75), inset 0 1px 0 rgba(255,255,255,0.15)`,
                  border: `1px solid ${BLOOD_HI}`,
                }}
                data-testid="bots-cta-free"
              >
                <span
                  className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)" }}
                >
                  <Sparkles size={16} color="white" />
                </span>
                <span className="flex flex-col items-start leading-none">
                  <span className="text-[10px] font-black uppercase tracking-[0.28em] text-white/80">Get a</span>
                  <span className="text-lg font-black text-white">FREE BOT</span>
                </span>
                <ArrowRight size={18} color="white" className="ml-2 transition-transform group-hover:translate-x-1" />
              </a>

              <a
                href="tel:+12148155966"
                className="inline-flex items-center gap-2 px-5 h-14 rounded-xl border font-semibold text-sm"
                style={{
                  borderColor: "rgba(220,38,38,0.55)",
                  color: OFFWHITE,
                  background: "rgba(220,38,38,0.05)",
                }}
                data-testid="bots-cta-call"
              >
                <Phone size={14} /> (214) 815-5966
              </a>
            </div>

            {/* Handwritten annotation */}
            <div
              className="mt-6 text-[13px] italic max-w-[280px]"
              style={{ color: FADE, fontFamily: "'Kalam','Caveat','Comic Sans MS',cursive" }}
            >
              Smarter workflows. <br />
              <span style={{ color: BLOOD_HI }}>Bigger results.</span>
            </div>
          </div>

          {/* RIGHT — Cyborg bot visual */}
          <div className="relative order-first lg:order-last">
            <BotVisual />
          </div>
        </div>
      </section>

      {/* Waveform divider */}
      <div className="relative z-10">
        <div
          aria-hidden
          className="mx-auto max-w-[1400px] px-6 md:px-10"
        >
          <svg
            viewBox="0 0 1200 40" className="w-full" preserveAspectRatio="none"
            style={{ height: 40, filter: `drop-shadow(0 0 12px ${RED_GLOW})` }}
          >
            <path
              d="M0,20 Q60,4 120,20 T240,20 T360,20 T480,20 T600,20 T720,20 T840,20 T960,20 T1080,20 T1200,20"
              fill="none" stroke={BLOOD} strokeWidth="1.5" opacity="0.7"
            />
          </svg>
        </div>
      </div>

      {/* 3 BOT CARDS */}
      <section className="relative z-10 py-14 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {BOTS.map((b) => (
              <BotCard key={b.key} bot={b} />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom answer strip */}
      <section className="relative z-10 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div
            className="rounded-2xl p-5 md:p-6 border flex flex-wrap items-center gap-x-8 gap-y-4"
            style={{
              background: PANEL,
              borderColor: "rgba(220,38,38,0.35)",
              boxShadow: `inset 0 0 60px -20px ${RED_GLOW}`,
            }}
          >
            <div
              className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
              style={{ border: `1.5px solid ${BLOOD}`, color: BLOOD }}
            >
              <Target size={20} />
            </div>
            <div className="flex-1 min-w-[240px] text-sm md:text-base font-semibold" style={{ color: OFFWHITE }}>
              WHATEVER YOUR BIGGEST HEADACHE IS,{" "}
              <span style={{ color: BLOOD_HI, fontWeight: 900 }}>YOUR AI TWIN</span>{" "}
              IS THE ANSWER.
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {PROOF.map(({ icon: I, label }) => (
                <div key={label} className="inline-flex items-center gap-1.5 text-sm">
                  <I size={14} style={{ color: BLOOD }} />
                  <span style={{ color: OFFWHITE }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final FREE BOT CTA banner */}
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="mx-auto max-w-[900px] px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.26em] mb-4"
               style={{ background: `rgba(220,38,38,0.14)`, color: BLOOD_HI, border: `1px solid ${BLOOD}66` }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: BLOOD_HI, boxShadow: `0 0 10px ${BLOOD}` }} />
            Free · limited · first come, first bot
          </div>
          <h2 className="font-black tracking-[-0.015em] leading-[0.98] uppercase"
              style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", color: OFFWHITE }}>
            Skip the meetings.{" "}
            <span style={{ color: BLOOD }}>Get a bot.</span>{" "}
            Change your business by tomorrow.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="tel:+12148155966"
              className="group inline-flex items-center gap-3 pl-4 pr-5 h-14 rounded-xl overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${BLOOD} 0%, ${RED_LOW} 100%)`,
                boxShadow: `0 24px 60px -20px rgba(220,38,38,0.75)`,
                border: `1px solid ${BLOOD_HI}`,
              }}
            >
              <span className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <Zap size={16} color="white" />
              </span>
              <span className="flex flex-col items-start leading-none">
                <span className="text-[10px] font-black uppercase tracking-[0.28em] text-white/80">Claim a</span>
                <span className="text-lg font-black text-white">FREE BOT</span>
              </span>
              <ArrowRight size={18} color="white" className="ml-2 transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/case-studies/miss-rodriguez"
              className="inline-flex items-center gap-2 px-5 h-14 rounded-xl border font-semibold text-sm"
              style={{ borderColor: "rgba(220,38,38,0.55)", color: OFFWHITE }}
            >
              See a bot in action <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <PlugitAIChat />
      <MobilePocketPopup />
    </div>
  );
}

// ─── Top nav (blood red) ────────────────────────────────────────────────────
function TopNav({ menuOpen, setMenuOpen }) {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: "rgba(5,5,5,0.86)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid rgba(220,38,38,0.35)`,
        boxShadow: `0 20px 60px -30px ${RED_GLOW}`,
      }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-9 md:h-11 w-auto" />
          <span className="hidden sm:inline text-[9px] uppercase tracking-[0.28em]" style={{ color: BLOOD_HI }}>
            /Bots
          </span>
        </Link>

        {/* Desktop nav — blood red as requested */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) =>
            n.primary ? (
              <a
                key={n.label}
                href={n.to}
                className="ml-2 inline-flex items-center gap-2 px-4 h-10 rounded-full font-black text-[11px] uppercase tracking-[0.24em]"
                style={{
                  background: BLOOD,
                  color: "white",
                  boxShadow: `0 12px 28px -10px ${RED_GLOW}`,
                }}
                data-testid="bots-nav-cta"
              >
                <Sparkles size={12} /> {n.label}
              </a>
            ) : (
              <Link
                key={n.label}
                to={n.to}
                className="px-3 h-10 inline-flex items-center rounded-md text-[12px] font-black uppercase tracking-[0.22em] hover:bg-white/[0.04] transition-colors"
                style={{ color: BLOOD_HI }}
              >
                {n.label}
              </Link>
            )
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ border: `1px solid ${BLOOD}66`, color: BLOOD_HI, background: "rgba(220,38,38,0.08)" }}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="md:hidden border-t"
          style={{ background: "rgba(5,5,5,0.96)", borderColor: `${BLOOD}44` }}
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-between h-11 px-3 rounded-lg text-[13px] font-black uppercase tracking-[0.22em]"
                style={{
                  color: n.primary ? "white" : BLOOD_HI,
                  background: n.primary ? BLOOD : "transparent",
                  border: n.primary ? `1px solid ${BLOOD_HI}` : `1px solid transparent`,
                }}
              >
                {n.label} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

// ─── BotCard ────────────────────────────────────────────────────────────────
function BotCard({ bot }) {
  const Icon = bot.icon;
  return (
    <div
      className="relative rounded-2xl p-5 md:p-6 border overflow-hidden transition-transform hover:-translate-y-1"
      style={{
        background: PANEL,
        borderColor: "rgba(220,38,38,0.35)",
        boxShadow: `inset 0 0 60px -30px ${RED_GLOW}, 0 30px 60px -30px rgba(0,0,0,0.6)`,
      }}
      data-testid={`bots-card-${bot.key}`}
    >
      {/* Corner red accent */}
      <span
        aria-hidden
        className="absolute -top-px -left-px w-10 h-10 rounded-tl-2xl"
        style={{ borderLeft: `2px solid ${BLOOD}`, borderTop: `2px solid ${BLOOD}` }}
      />
      <span
        aria-hidden
        className="absolute -bottom-px -right-px w-10 h-10 rounded-br-2xl"
        style={{ borderRight: `2px solid ${BLOOD}`, borderBottom: `2px solid ${BLOOD}` }}
      />

      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(135deg, rgba(220,38,38,0.14) 0%, rgba(0,0,0,0.4) 100%)",
            border: `1px solid ${BLOOD}88`,
            boxShadow: `0 10px 24px -8px ${RED_GLOW}`,
          }}
        >
          <Icon size={20} color={BLOOD_HI} />
        </div>
        <div>
          <div className="text-[9px] font-black uppercase tracking-[0.28em]" style={{ color: BLOOD }}>
            AI Twin
          </div>
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight" style={{ color: OFFWHITE }}>
            {bot.title}
          </h3>
        </div>
      </div>

      <div className="mt-4 space-y-0.5 leading-tight">
        <div className="font-bold text-base md:text-lg" style={{ color: OFFWHITE }}>{bot.line1}</div>
        <div className="font-bold text-base md:text-lg" style={{ color: OFFWHITE }}>{bot.line2}</div>
      </div>

      <p className="mt-3 text-[13px] leading-relaxed" style={{ color: DIM }}>
        {bot.body}
      </p>

      <a
        href="tel:+12148155966"
        className="mt-5 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em]"
        style={{ color: BLOOD_HI }}
      >
        Get {bot.title.toLowerCase()} free <ArrowRight size={12} />
      </a>
    </div>
  );
}

// ─── Bot visual — real photorealistic human ⇄ cyborg morph ────────────────
function BotVisual() {
  return (
    <div className="relative aspect-[7/8] max-h-[620px] mx-auto">
      {/* Red glow behind head */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(320px 260px at 60% 42%, ${RED_GLOW}, transparent 65%)`,
        }}
      />

      {/* Morph stack — human below, bot above, cross-fading forever (no box) */}
      <div className="relative w-full h-full">
        <img
          src="/bots/human.jpg"
          alt="45-year-old man before his AI twin takes over"
          className="absolute inset-0 w-full h-full object-cover object-right"
          style={{ animation: "botMorphHuman 10s ease-in-out infinite" }}
          loading="eager"
        />
        <img
          src="/bots/bot.jpg"
          alt="Black chrome cyborg with red visor — the AI twin"
          className="absolute inset-0 w-full h-full object-cover object-right"
          style={{ animation: "botMorphBot 10s ease-in-out infinite" }}
          loading="eager"
          data-testid="bots-hero-bot-img"
        />

        {/* Feathered edges — soft fade to transparent so the images bleed into the page */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background: `radial-gradient(ellipse 75% 85% at 65% 50%, transparent 55%, ${INK} 100%)`,
          }}
        />

        {/* Scan-line glitch pass — runs across the crossfade window */}
        <div
          aria-hidden
          className="absolute inset-x-0 pointer-events-none z-10 h-16 opacity-0"
          style={{
            background: `linear-gradient(180deg, transparent 0%, ${BLOOD_HI}55 45%, ${BLOOD_HI}88 50%, ${BLOOD_HI}55 55%, transparent 100%)`,
            mixBlendMode: "screen",
            filter: "blur(6px)",
            animation: "botScanSweep 10s ease-in-out infinite",
          }}
        />
        {/* Digital noise flicker at the moment of transformation */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none z-10 opacity-0"
          style={{
            background:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)",
            mixBlendMode: "overlay",
            animation: "botStaticFlicker 10s ease-in-out infinite",
          }}
        />

        {/* "PHASE" tag — animates label along with the morph */}
        <div
          className="absolute top-3 left-3 z-20 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-[0.28em]"
          style={{
            background: "rgba(0,0,0,0.6)",
            border: `1px solid ${BLOOD}66`,
            color: BLOOD_HI,
            backdropFilter: "blur(4px)",
            animation: "botPhaseLabel 10s ease-in-out infinite",
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: BLOOD_HI, boxShadow: `0 0 6px ${BLOOD_HI}` }} />
          <span data-testid="bots-hero-phase-label">Before → After</span>
        </div>
      </div>

      {/* Floating chat bubbles */}
      <div
        className="absolute z-20 top-4 -right-2 md:-right-6 max-w-[240px] rounded-xl p-3"
        style={{
          background: "rgba(11,11,15,0.9)",
          border: `1px solid ${BLOOD}55`,
          boxShadow: `0 20px 50px -20px ${RED_GLOW}`,
          backdropFilter: "blur(6px)",
        }}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: BLOOD_HI, boxShadow: `0 0 8px ${BLOOD}` }} />
          <span className="text-[9px] font-black uppercase tracking-[0.28em]" style={{ color: BLOOD_HI }}>AI Twin</span>
        </div>
        <div className="text-xs" style={{ color: DIM }}>Always on. Always working.</div>
      </div>

      <div
        className="absolute z-20 top-1/3 -right-2 md:-right-10 max-w-[220px] rounded-xl px-3 py-2"
        style={{
          background: "rgba(11,11,15,0.85)",
          border: `1px solid rgba(255,255,255,0.08)`,
          backdropFilter: "blur(6px)",
        }}
      >
        <div className="text-[12px] leading-snug" style={{ color: OFFWHITE }}>
          What process is slowing you down?
        </div>
      </div>

      <div
        className="absolute z-20 bottom-10 -right-2 md:-right-6 max-w-[220px] rounded-xl px-3 py-2"
        style={{
          background: `linear-gradient(135deg, ${BLOOD} 0%, ${RED_LOW} 100%)`,
          border: `1px solid ${BLOOD_HI}`,
          boxShadow: `0 20px 50px -20px ${RED_GLOW}`,
        }}
      >
        <div className="text-[12px] leading-snug text-white font-semibold">
          Let&apos;s build a solution. Tailored for you.
        </div>
      </div>

      {/* Waveform floor */}
      <svg
        viewBox="0 0 400 40" className="absolute -bottom-2 left-0 right-0 w-full opacity-70"
        style={{ filter: `drop-shadow(0 0 10px ${RED_GLOW})` }}
        aria-hidden
      >
        <path
          d="M0,20 Q25,4 50,20 T100,20 T150,20 T200,20 T250,20 T300,20 T350,20 T400,20"
          fill="none" stroke={BLOOD} strokeWidth="1.5"
        />
      </svg>
    </div>
  );
}
